// client/src/coms/workspace/useWorkspace.js
// Conversation tabs for the signed-in account: restored from the server (/api/coms/me/workspace) with a
// per-account local cache, validated against live data (servers, channels, DM threads) so a conversation the
// user lost access to is closed rather than shown, and decorated with live names and unread/mention badges.
// Background tabs are not mounted, so they open no message stream and never mark anything read.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { comsApiGet, comsApiPut } from '../comsApi';
import * as M from './workspaceModel';
import { ENGAGED_EVENT } from './engagement';

const SAVE_DELAY_MS = 900;
const CACHE_PREFIX = 'vx:ws:u:';
const READ_POLL_VISIBLE_MS = 5000;
const READ_POLL_HIDDEN_MS = 20000;
const DM_POLL_MS = 15000;

function readCache(key) {
  try {
    const raw = window.localStorage.getItem(key);
    return raw ? M.normalizeWorkspace(JSON.parse(raw)) : null;
  } catch {
    return null;
  }
}

function serverKeyOf(s) {
  if (!s || typeof s !== 'object') return '';
  return String(s.id != null ? s.id : s.public_id != null ? s.public_id : s.server_key != null ? s.server_key : '').trim();
}

function channelDbId(channelId) {
  const m = /^(?:text|voice):(\d+)$/.exec(String(channelId || ''));
  return m ? Number(m[1]) : 0;
}

export default function useWorkspace({ user, servers, serversLoaded, restore = true }) {
  const userId = user && user.id != null ? String(user.id) : '';
  const cacheKey = userId ? `${CACHE_PREFIX}${userId}` : '';
  const [ws, setWs] = useState(() => (restore && cacheKey ? readCache(cacheKey) : null) || M.EMPTY_WORKSPACE);
  const [loaded, setLoaded] = useState(false);
  const [threads, setThreads] = useState(null); // null = not loaded yet
  const [readStates, setReadStates] = useState({}); // serverKey -> { channelDbId: { unread_count, mention_count } }
  const [notice, setNotice] = useState('');
  const saveTimerRef = useRef(null);
  const wsRef = useRef(ws);
  wsRef.current = ws;
  const dirtyRef = useRef(false);

  // ---------- load ----------
  useEffect(() => {
    if (!userId) return undefined;
    let alive = true;
    setLoaded(false);
    setWs((restore && readCache(`${CACHE_PREFIX}${userId}`)) || M.EMPTY_WORKSPACE);
    comsApiGet('/me/workspace')
      .then((res) => {
        if (!alive) return;
        if (!restore || !res || !res.stored) return;
        // A local change made before the response arrived is merged with the stored copy, never instead of it.
        if (!dirtyRef.current) setWs(M.normalizeWorkspace(res.workspace));
        else update((cur) => M.mergeWorkspace(res.workspace, cur));
      })
      .catch(() => {})
      .finally(() => alive && setLoaded(true));
    return () => {
      alive = false;
    };
    // `update` is stable (it only depends on flush); listing it would reload the workspace on every save.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, restore]);

  // ---------- save ----------
  const flush = useCallback(
    ({ keepalive = false } = {}) => {
      saveTimerRef.current = null;
      if (!userId) return;
      const doc = wsRef.current;
      try {
        window.localStorage.setItem(`${CACHE_PREFIX}${userId}`, JSON.stringify(doc));
      } catch {}
      if (keepalive) {
        // Leaving or reloading the page: the request must outlive it.
        fetch('/api/coms/me/workspace', { method: 'PUT', credentials: 'include', keepalive: true, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ workspace: doc }) }).catch(() => {});
        return;
      }
      comsApiPut('/me/workspace', { workspace: doc }).catch(() => {});
    },
    [userId]
  );

  useEffect(() => {
    const onHide = () => {
      if (!saveTimerRef.current) return;
      clearTimeout(saveTimerRef.current);
      flush({ keepalive: true });
    };
    window.addEventListener('pagehide', onHide);
    return () => {
      window.removeEventListener('pagehide', onHide);
      if (saveTimerRef.current) {
        clearTimeout(saveTimerRef.current);
        flush();
      }
    };
  }, [flush]);

  const update = useCallback(
    (fn) => {
      setWs((prev) => {
        const next = fn(prev);
        if (M.tabsEqual(prev, next)) return prev;
        dirtyRef.current = true;
        wsRef.current = next;
        if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
        saveTimerRef.current = setTimeout(() => flush(), SAVE_DELAY_MS);
        return next;
      });
    },
    [flush]
  );

  // ---------- validation against live data ----------
  const hasDmTabs = ws.tabs.some((t) => t.k === 'd');

  const loadThreads = useCallback(async () => {
    try {
      const res = await comsApiGet(`/dms?_=${Date.now()}`);
      setThreads(Array.isArray(res && res.threads) ? res.threads : []);
    } catch (e) {
      // Only an authoritative answer may close tabs; network errors keep them.
      if (e && (e.status === 401 || e.status === 403)) setThreads([]);
    }
  }, []);

  useEffect(() => {
    if (!userId || !hasDmTabs) return undefined;
    loadThreads();
    const t = setInterval(loadThreads, DM_POLL_MS);
    return () => clearInterval(t);
  }, [userId, hasDmTabs, loadThreads]);

  const serverByKey = useMemo(() => {
    const map = new Map();
    for (const s of Array.isArray(servers) ? servers : []) map.set(serverKeyOf(s), s);
    return map;
  }, [servers]);

  useEffect(() => {
    if (!serversLoaded || !Array.isArray(servers)) return;
    const isValid = (tab) => {
      if (tab.k === 'c') {
        const s = serverByKey.get(tab.s);
        if (!s) return false;
        const channels = Array.isArray(s.channels) ? s.channels : [];
        return channels.some((c) => String(c.id) === tab.c);
      }
      if (tab.k === 'd') {
        if (threads === null) return true; // not checked yet
        return threads.some((th) => String(th && th.id) === tab.t);
      }
      return false;
    };
    const { ws: next, removed } = M.pruneTabs(wsRef.current, isValid);
    if (removed.length) {
      update(() => next);
      setNotice(removed.length === 1 ? 'A tab was closed because you no longer have access to it.' : `${removed.length} tabs were closed because you no longer have access to them.`);
    }
  }, [servers, serversLoaded, serverByKey, threads, update]);

  // A stream reporting lost access closes that conversation's tab immediately.
  useEffect(() => {
    const onRevoked = (e) => {
      const d = (e && e.detail) || {};
      const id = d.serverKey && d.channelId ? M.tabIdOf({ k: 'c', s: String(d.serverKey), c: String(d.channelId) }) : '';
      if (!id) return;
      update((w) => M.pruneTabs(w, (t) => M.tabIdOf(t) !== id).ws);
      setNotice('A tab was closed because you no longer have access to it.');
    };
    window.addEventListener('vox-channel-access-revoked', onRevoked);
    return () => window.removeEventListener('vox-channel-access-revoked', onRevoked);
  }, [update]);

  // Writing in a conversation (a non-empty draft typed, or something sent) keeps its tab: the preview tab
  // becomes a saved tab that browsing no longer replaces. A restored draft never sends this (engagement.js).
  useEffect(() => {
    const onEngaged = (e) => {
      const id = String((e && e.detail && e.detail.id) || '');
      if (id && wsRef.current.preview === id) update((w) => M.promoteTab(w, id));
    };
    window.addEventListener(ENGAGED_EVENT, onEngaged);
    return () => window.removeEventListener(ENGAGED_EVENT, onEngaged);
  }, [update]);

  // ---------- unread / mention badges ----------
  const tabServerKeys = useMemo(() => Array.from(new Set(ws.tabs.filter((t) => t.k === 'c').map((t) => t.s))).sort().join(','), [ws.tabs]);

  useEffect(() => {
    if (!userId || !tabServerKeys) return undefined;
    let alive = true;
    let timer = null;
    const keys = tabServerKeys.split(',');
    const run = async () => {
      const next = {};
      for (const key of keys) {
        try {
          const res = await comsApiGet(`/servers/${encodeURIComponent(key)}/channel-read-states`);
          next[key] = (res && res.states) || {};
        } catch {}
      }
      if (!alive) return;
      setReadStates((prev) => (JSON.stringify(prev) === JSON.stringify(next) ? prev : next));
      timer = setTimeout(run, document.visibilityState === 'hidden' ? READ_POLL_HIDDEN_MS : READ_POLL_VISIBLE_MS);
    };
    run();
    return () => {
      alive = false;
      if (timer) clearTimeout(timer);
    };
  }, [userId, tabServerKeys]);

  // ---------- decorated tabs ----------
  const items = useMemo(
    () =>
      ws.tabs.map((tab) => {
        const id = M.tabIdOf(tab);
        if (tab.k === 'c') {
          const s = serverByKey.get(tab.s);
          const ch = s && Array.isArray(s.channels) ? s.channels.find((c) => String(c.id) === tab.c) : null;
          const st = (readStates[tab.s] || {})[channelDbId(tab.c)] || {};
          return {
            id,
            tab,
            kind: 'channel',
            title: ch && ch.name ? String(ch.name) : 'Channel',
            serverName: s && s.name ? String(s.name) : '',
            server: s || null,
            channel: ch || null,
            isVoice: String((ch && ch.kind) || '') === 'voice',
            unread: Number(st.unread_count) > 0 && Number(st.show_notifications) !== 0,
            mentions: Math.max(0, Number(st.mention_count) || 0),
            pinned: !!tab.p,
            preview: ws.preview === id,
          };
        }
        const th = Array.isArray(threads) ? threads.find((x) => String(x && x.id) === tab.t) : null;
        const other = th && th.other_user ? th.other_user : {};
        return {
          id,
          tab,
          kind: 'dm',
          title: String((th && (th.username || th.label || other.username)) || 'Direct message'),
          serverName: '',
          otherUserId: th ? Number(th.other_user_id || other.id) || 0 : 0,
          unread: !!(th && th.has_unread),
          mentions: 0,
          pinned: !!tab.p,
          preview: ws.preview === id,
        };
      }),
    [ws.tabs, ws.preview, serverByKey, readStates, threads]
  );

  const api = useMemo(
    () => ({
      open: (tab, opts) => update((w) => M.openTab(w, tab, opts)),
      activate: (id) => update((w) => M.activateTab(w, id)),
      // Typing, sending or "Keep tab open": a preview tab becomes a saved tab.
      promote: (id) => update((w) => M.promoteTab(w, id)),
      close: (id) => update((w) => M.closeTab(w, id)),
      closeOthers: (id) => update((w) => M.closeOtherTabs(w, id)),
      reopenClosed: () => update((w) => M.reopenClosedTab(w)),
      move: (id, toIndex) => update((w) => M.moveTab(w, id, toIndex)),
      moveBy: (id, delta) => update((w) => M.moveTabBy(w, id, delta)),
      pin: (id, pinned) => update((w) => M.setPinned(w, id, pinned)),
      cycle: (delta) => update((w) => M.cycleTab(w, delta)),
      openSplit: (id) => update((w) => M.openSplit(w, id)),
      setSecondary: (id) => update((w) => M.setSecondary(w, id)),
      closeSplit: () => update((w) => M.closeSplit(w)),
      swapSplit: () => update((w) => M.swapSplit(w)),
      replace: (next) => update(() => M.normalizeWorkspace(next)),
    }),
    [update]
  );

  return { ws, items, loaded, notice, clearNotice: () => setNotice(''), refreshThreads: loadThreads, ...api };
}
